import { useEffect, useState } from 'react';
import { refreshOrderEverywhere } from '../../lib/orderQueries';
import { useNavigate } from 'react-router-dom';
import { Modal, Input, Button, Select, Tag, message } from '../../ui';
import { AttachmentCell } from '../../components/AttachmentPreview';
import { usePermission } from '../../store/auth';
import { Check, X } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { request } from '../../api/client';
import { bankAcctOption, bankAcctLabel } from './PaymentFormModal';
import { inr2 } from '../../lib/format';
import { formatIst, formatIstDate, formatIstTime, toIstDateString } from '../../lib/time';
import { orderCoverage, submissionKind } from './PaymentsListPage';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex border-b border-line last:border-b-0">
      <div className="w-32 shrink-0 bg-surface-2 px-3 py-2 text-xs text-muted">{label}</div>
      <div className="flex-1 px-3 py-2 text-sm text-content">{children}</div>
    </div>
  );
}

const STATUS: Record<string, { tone: 'gold' | 'green' | 'red'; label: string }> = {
  PENDING: { tone: 'gold', label: 'Pending approval' },
  APPROVED: { tone: 'green', label: 'Approved' },
  REJECTED: { tone: 'red', label: 'Rejected' },
};

/**
 * Approver's review of a submitted payment — Tailwind layout. Everything needed to verify against the
 * bank before approving: UTR/reference, proof image(s), and the order's paid/due context (so a duplicate
 * or over-payment is obvious). Approve posts the ledger credit; Reject needs a reason.
 *
 * It is also the payment's READ view. A register row opens it for every payment, and only a PENDING one
 * in front of somebody who may approve is a decision; anything else (already approved or rejected, or a
 * viewer without PAYMENT.APPROVE) shows the same facts with the status, and no bank picker or buttons —
 * offering Approve on a decided payment would ask a question that has already been answered.
 */
export function PaymentReviewModal({ payment, open, onClose }: { payment: any | null; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canApprove = usePermission('PAYMENT.APPROVE');
  const decide = !!payment && payment.approvalStatus === 'PENDING' && canApprove;
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const { data: summary } = useQuery({
    queryKey: ['order-pay-summary', payment?.orderId],
    queryFn: () => request<any>({ url: `/orders/${payment.orderId}/payment-summary` }),
    enabled: open && decide && !!payment?.orderId,
  });

  // The verifier confirms WHERE the money landed (seller bank account) before approving — mandatory for
  // every non-cash payment (a retailer's submission never sets this; Accounts reconciles it here).
  const { data: bankAccounts } = useQuery({
    queryKey: ['bank-account-options'],
    queryFn: () => request<any[]>({ url: '/company/bank-accounts/options' }),
    enabled: open,
  });
  const bankOpts: any[] = Array.isArray(bankAccounts) ? bankAccounts : ((bankAccounts as any)?.data ?? []);
  const [bankId, setBankId] = useState<string | undefined>();
  useEffect(() => { setBankId(payment?.receivedBankAccountId ? String(payment.receivedBankAccountId) : undefined); }, [payment?.id]);
  /* The payment's OWN mode decides whether there is an account to name. Cash was collected in notes:
     it lands in nobody's account, and the picker was asking the verifier to say which one anyway. */
  const bankApplies = !!payment && String(payment.mode ?? '').toUpperCase() !== 'CASH';
  const bankSelectOpts = (() => {
    const opts = bankOpts.map(bankAcctOption) as any[];
    const cur = payment?.receivedBank;
    if (cur?.id && !opts.some((o: any) => o.value === String(cur.id))) {
      opts.unshift({ value: String(cur.id), searchLabel: bankAcctLabel(cur), label: `${cur.bankName || cur.label || 'Bank'}${cur.last4 ? ` ··${cur.last4}` : ''} (inactive)` });
    }
    return opts;
  })();

  const invalidate = () => {
    ['payments', 'orders', 'retailer-ledger', 'badges'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    qc.invalidateQueries({ queryKey: ['order-payments', payment?.orderId] });
    // Approving can lock the advance discount and rewrite the order's totals — refresh the whole order.
    refreshOrderEverywhere(qc, payment?.orderId);
  };
  const approve = useConfirmedMutation({ title: "Approve this payment and post it to the ledger?", content: "The retailer’s outstanding drops by this amount immediately.", okText: "Yes, approve" }, {
    mutationFn: () => request({ url: `/payments/${payment.id}/approve`, method: 'POST', data: { receivedBankAccountId: bankApplies ? (bankId || undefined) : undefined } }),
    onSuccess: () => { message.success('Payment approved — ledger updated'); invalidate(); onClose(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to approve'),
  });
  const reject = useConfirmedMutation({ title: "Reject this payment?", content: "Nothing is posted to the ledger. Your reason goes back to whoever recorded it.", okText: "Yes, reject", danger: true }, {
    mutationFn: () => request({ url: `/payments/${payment.id}/reject`, method: 'POST', data: { reason } }),
    onSuccess: () => { message.success('Payment rejected'); setRejecting(false); setReason(''); invalidate(); onClose(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to reject'),
  });

  if (!payment) return null;
  const files: string[] = payment.attachmentUrls?.length ? payment.attachmentUrls : (payment.attachmentUrl ? [payment.attachmentUrl] : []);
  const submitterName = payment.submittedByName || payment.retailerName || '—';
  const isRetailerSubmitter = payment.submittedByRole === 'Retailer';
  /** One sentence naming the whole act — same vocabulary the register uses. */
  const act = (() => {
    const kind = submissionKind(payment);
    if (!kind) return null;
    const cov = orderCoverage(payment);
    const against = payment.orderNo ? ` against order ${payment.orderNo}` : ' on account (no order named)';
    // A credit application moved no money — it must not be described as anyone collecting or paying.
    if (payment.fromCredit) {
      return `${inr2(payment.amount)} applied${against} from the retailer’s account credit — no money changed hands`;
    }
    if (isRetailerSubmitter) {
      return `paid ${inr2(payment.amount)}${against}${cov ? ` — ${cov.text}` : ''}`;
    }
    /* A rep who watched the retailer pay by UPI RECORDED it; a rep who took notes COLLECTED it and
       is now carrying company cash. The approver needs to know which, because only one of them
       leaves money outside the bank. */
    const cash = String(payment.mode ?? '').toUpperCase() === 'CASH' && !payment.receivedBank;
    // "collected ₹3,864 from X" / "recorded ₹3,864 on behalf of X" — the amount after the verb in both
    // (it read "recorded on behalf of ₹3,864 X").
    const who = payment.retailerName ?? 'the retailer';
    const act = cash ? `collected ${inr2(payment.amount)} from ${who}` : `recorded ${inr2(payment.amount)} on behalf of ${who}`;
    return `${act}${against}${cov ? ` — ${cov.text}` : ''}`;
  })();
  const amt = Number(payment.amount);
  const due = Number(summary?.due ?? 0);
  const afterApprove = due - amt;

  return (
    /*
     * WIDE ENOUGH FOR THE FIELD THAT TAKES INPUT.
     *
     * At 780px the details column resolved to ~410px, and a 128px label column left the values about
     * 280px: the retailer's name broke across two lines, and the ONE control in this dialog — the
     * bank picker, which is required before a non-cash payment can be approved — was squeezed to the
     * same 280px with its red "required" note cramming underneath it. The dialog is a verification
     * screen; the thing being verified should not be the tightest thing on it. The extra width goes
     * to the details side (1.5fr vs 1fr), because the proof panel beside it is a thumbnail.
     */
    <Modal open={open} onOpenChange={(o) => { if (!o) { setRejecting(false); onClose(); } }} width={980} title={decide ? `Review payment ${payment.paymentNo}` : `Payment ${payment.paymentNo}`}>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-[1.5fr_1fr]">
          {/* Details */}
          <div className="overflow-hidden rounded-card border border-line">
            <Field label="Retailer">{payment.retailerName}{payment.retailerCode ? ` · ${payment.retailerCode}` : ''}</Field>
            <Field label="Order">
              {payment.orderNo ? (
                <button
                  type="button"
                  onClick={() => { onClose(); navigate(`/orders/${payment.orderId}`); }}
                  className="inline-flex items-center rounded-pill bg-sky-500/10 px-2 py-0.5 type-label text-sky-600 hover:bg-sky-500/20"
                >
                  {payment.orderNo}
                </button>
              ) : <span className="text-muted">On-account</span>}
            </Field>
            <Field label="Amount"><span className="type-metric-sm">{inr2(payment.amount)}</span></Field>
            {!decide && (
              <Field label="Status">
                <Tag tone={STATUS[payment.approvalStatus]?.tone ?? 'neutral'} style={{ margin: 0 }}>{STATUS[payment.approvalStatus]?.label ?? payment.approvalStatus}</Tag>
                {payment.approvalStatus === 'APPROVED' && payment.approvedAt && (
                  <span className="ml-2 type-caption text-subtle">on {formatIst(payment.approvedAt, 'DD MMM YYYY, hh:mm A')}</span>
                )}
                {payment.approvalStatus === 'REJECTED' && payment.rejectedReason && (
                  <div className="mt-1 type-caption text-danger-600">{payment.rejectedReason}</div>
                )}
              </Field>
            )}
            <Field label="Mode">{payment.mode}</Field>
            {/* The stored value is a raw `2026-09-09` — a database string, not a date anybody reads.
                Formatted like every other date in the app, with WHEN IT WAS RECORDED beneath it:
                payment_date is a DATE column carrying no time, so the only real clock reading on a
                payment is its created_at, and the verifier is the person who most needs it. */}
            <Field label="Date">
              {formatIstDate(payment.paymentDate)}
              {payment.createdAt && (
                <span className="ml-2 type-caption text-subtle">
                  {/* The day is already on this line — repeating it to carry a time reads as two
                      dates. Only a back-dated payment, where the two really differ, spells it out. */}
                  recorded {toIstDateString(payment.createdAt) === String(payment.paymentDate ?? '').slice(0, 10)
                    ? formatIstTime(payment.createdAt)
                    : formatIst(payment.createdAt, 'DD MMM, hh:mm A')}
                </span>
              )}
            </Field>
            <Field label="Reference No">{payment.referenceNo || '—'}</Field>
            <Field label="UTR / Bank Ref"><span className="font-semibold">{payment.utr || '—'}</span></Field>
            {!decide ? (
              <Field label="Received in">
                {payment.receivedBank
                  ? `${payment.receivedBank.bankName || payment.receivedBank.label || 'Bank'}${payment.receivedBank.last4 ? ` ··${payment.receivedBank.last4}` : ''}`
                  : payment.fromCredit ? 'Account credit — no money received'
                    : bankApplies ? <span className="text-subtle">—</span> : 'Cash — no bank account'}
              </Field>
            ) : bankApplies ? (
              <Field label="Received in *">
                <div>
                  <Select
                    allowClear showSearch optionFilterProp="searchLabel" size="small"
                    style={{ width: '100%' }}
                    placeholder={bankSelectOpts.length ? 'Select the receiving bank account' : 'No bank accounts configured'}
                    disabled={!bankSelectOpts.length}
                    value={bankId}
                    onChange={(v: any) => setBankId(v || undefined)}
                    options={bankSelectOpts}
                    status={!bankId ? 'error' : undefined}
                  />
                  {!bankId && (
                    <div className="mt-1 max-w-prose type-caption leading-snug text-danger-600">
                      Confirm which account received it before approving.
                    </div>
                  )}
                </div>
              </Field>
            ) : (
              /* Still SAID, because "where did it land" is a real question about a collection — it is
                 just already answered, and answered by a fact rather than by the verifier. */
              <Field label="Received in">Cash — no bank account</Field>
            )}
            <Field label="Submitted by">
              <span className="inline-flex items-center gap-2">
                {isRetailerSubmitter ? (
                  <button type="button" onClick={() => { onClose(); navigate(`/retailers/${payment.retailerId}`); }} className="text-content hover:text-brand-700 hover:underline">
                    {submitterName}
                  </button>
                ) : <span>{submitterName}</span>}
                {payment.submittedByRole && (
                  <span className="inline-flex items-center rounded-pill bg-brand-600/10 px-2 py-0.5 type-caption text-brand-700 dark:text-brand-300">{payment.submittedByRole}</span>
                )}
              </span>
              {/* The act, spelled out for the person about to approve it: who took the money, from
                  whom, against which order, and whether that clears the order or leaves a balance.
                  Everything here is on the screen already — but scattered across six fields the
                  approver has to assemble in their head. */}
              {act && <div className="mt-1 type-caption text-muted">{act}</div>}
            </Field>
          </div>

          {/* Proof */}
          <div>
            <div className="mb-2 type-body-strong text-content">Payment proof</div>
            {/* The evidence this decision rests on, all of it, previewing where the decision is made.
                A PDF receipt used to be a link into a new tab: the approver left the modal to read the
                one thing they were approving against. */}
            <AttachmentCell
              files={files}
              label="Payment proof"
              size={132}
              empty={<div className="rounded-card border border-warning-500/20 bg-warning-500/10 px-3 py-2 text-sm text-warning-600">No proof attached.</div>}
            />
          </div>
        </div>

        {/* Order context — split applied ACCOUNT CREDIT (ledger-neutral, no bank receipt) from CASH actually
            received, so the approver knows the credit isn't a transfer to reconcile and this cash clears the
            real balance. Falls back to the lumped "already paid" when no credit was applied. */}
        {/* Only for a decision: every line of it is phrased as "what approving would do", and on a
            decided payment the order's paid figure already includes this money. */}
        {decide && payment.orderId && summary && (
          <div className={`rounded-card border px-3 py-2.5 text-sm ${afterApprove < -0.01 ? 'border-danger-500/20 bg-danger-500/10 text-danger-600' : 'border-sky-500/20 bg-sky-500/10 text-sky-600'}`}>
            <div className="font-semibold">
              {Number(summary.creditApplied) > 0.01
                ? `Payable on the order ${inr2(summary.grandTotal)} · ${inr2(summary.creditApplied)} account credit applied · ${inr2(summary.cashPaid)} cash received`
                : `Payable on the order ${inr2(summary.grandTotal)} · already paid ${inr2(summary.paid)}`}
            </div>
            <div className="mt-0.5">
              {afterApprove < -0.01
                ? `This ${inr2(amt)} exceeds the ${inr2(due)} still due — approving would overpay. Check for a duplicate.`
                : afterApprove <= 0.01
                  ? `Approving this ${String(payment.mode ?? '').toUpperCase() === 'CASH' ? 'cash ' : ''}payment clears the order — remaining due becomes ₹0.`
                  : `After approval, ${inr2(afterApprove)} will still be due on the order.`}
            </div>
            {Number(summary.creditApplied) > 0.01 && (
              <div className="mt-1 text-xs opacity-80">
                The {inr2(summary.creditApplied)} account credit is the retailer's advance-in-credit — ledger-neutral, with no bank receipt to match. You're only verifying the {inr2(amt)} cash here.
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        {decide && (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line pt-3">
          {rejecting ? (
            <div className="flex w-full flex-col gap-2">
              <Input.TextArea rows={2} maxLength={255} placeholder="Reason for rejection (the submitter will see this — e.g. proof unclear, UTR mismatch)" value={reason} onChange={(e) => setReason(e.target.value)} />
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="default" onClick={() => setRejecting(false)}>Cancel</Button>
                <Button variant="danger" icon={<X size={16} />} loading={reject.isPending} disabled={!reason.trim()} onClick={() => reject.mutate()}>Confirm reject</Button>
              </div>
            </div>
          ) : (
            <>
              <Button variant="danger" icon={<X size={16} />} onClick={() => setRejecting(true)}>Reject</Button>
              <Button variant="primary" icon={<Check size={16} />} loading={approve.isPending} disabled={bankApplies && !bankId} onClick={() => approve.mutate()}>Approve &amp; post to ledger</Button>
            </>
          )}
        </div>
        )}
      </div>
    </Modal>
  );
}
